import { useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { notificationService, type NotificationItem } from '../services/notifications';
import { climbsService } from '../services/climbs';
import { scheduleEventsService } from '../services/schedule-events';
import FeedItemCard from '../components/home/FeedItemCard';
import { useNotificationContext } from '../contexts/notification-context';
import { useToast } from '../components/ui/use-toast';
import { PushNudgeCard } from '../components/push/PushNudgeCard';

const LIMIT = 25;

/**
 * True when the URL belongs to the other of the two apps in this bundle.
 *
 * Deliberately narrower than comparing origins: notification URLs are built
 * from FRONTEND_URL, whose host often differs from the one actually being
 * browsed (dev vs prod, localhost vs the Caddy alias). Stripping the origin
 * and routing client-side is what makes that work, so only a genuine
 * member-app ↔ gym-admin crossing warrants a full page load. The `gym.` host
 * check mirrors how main.tsx decides which app to render.
 */
function isCrossAppUrl(url: string): boolean {
  try {
    const target = new URL(url, window.location.href);
    return target.hostname.startsWith('gym.') !== window.location.hostname.startsWith('gym.');
  } catch {
    // Not an absolute URL — treat it as an in-app path.
    return false;
  }
}

export default function NotificationsPage() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [confirmingIds, setConfirmingIds] = useState<Set<string>>(new Set());
  const { decrementUnread, clearUnread } = useNotificationContext();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isAuthenticated) navigate('/sign-in');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    notificationService.getNotifications(undefined, LIMIT).then(res => {
      setItems(res.data);
      setNextBefore(res.meta.next_before);
      setHasMore(res.meta.has_more);
    }).catch(console.error).finally(() => setLoading(false));
  }, [isAuthenticated]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || !nextBefore) return;
    try {
      setLoadingMore(true);
      const res = await notificationService.getNotifications(nextBefore, LIMIT);
      setItems(prev => [...prev, ...res.data]);
      setNextBefore(res.meta.next_before);
      setHasMore(res.meta.has_more);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, nextBefore]);

  // A climb deleted from a push notification action button removes any matching
  // climb_reported_removed notifications still shown in the open list.
  useEffect(() => {
    const handler = (event: Event) => {
      const climbId = (event as CustomEvent<{ climbId?: string }>).detail?.climbId;
      if (!climbId) return;
      setItems(prev =>
        prev.filter(
          i =>
            !(i.type === 'climb_reported_removed' && i.attributes.subject.id === String(climbId)),
        ),
      );
    };
    window.addEventListener('sw-climb-deleted', handler);
    return () => window.removeEventListener('sw-climb-deleted', handler);
  }, []);

  useEffect(() => {
    if (!loadMoreRef.current || !hasMore) return;
    observerRef.current = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting) loadMore(); },
      { threshold: 0.1 }
    );
    observerRef.current.observe(loadMoreRef.current);
    return () => observerRef.current?.disconnect();
  }, [hasMore, loadMore]);

  const handleItemClick = (item: NotificationItem) => {
    if (!item.attributes.viewed) decrementUnread();
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, attributes: { ...i.attributes, viewed: true } } : i));
    notificationService.markViewed([item.id]).catch(() => {});
    const url = item.attributes.url;
    if (!url) return;

    // Some notifications point at the gym-admin app, which lives on another
    // host. Stripping the origin and handing that path to the router would
    // land on a route this app does not have, so those need a real navigation.
    if (isCrossAppUrl(url)) {
      window.location.href = url;
      return;
    }

    const path = url.replace(/^https?:\/\/[^/]+/, '');
    navigate(path || url);
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await notificationService.markViewed();
      setItems(prev => prev.map(i => ({ ...i, attributes: { ...i.attributes, viewed: true } })));
      clearUnread();
    } catch (err) {
      console.error(err);
    } finally {
      setMarkingAll(false);
    }
  };

  const handleDeleteClimb = async (item: NotificationItem) => {
    const climbId = item.attributes.subject.id;
    if (!climbId) return;
    if (!window.confirm(t('notifications.climbReportedRemoved.deleteConfirm'))) return;

    setDeletingIds(prev => new Set(prev).add(item.id));
    try {
      await climbsService.destroyClimb(climbId);
      if (!item.attributes.viewed) decrementUnread();
      notificationService.markViewed([item.id]).catch(() => {});
      setItems(prev => prev.filter(i => i.id !== item.id));
      toast({
        title: t('notifications.climbReportedRemoved.deleteSuccess'),
        duration: 3000,
      });
    } catch (err) {
      console.error(err);
      toast({
        title: t('notifications.climbReportedRemoved.deleteError'),
        variant: 'destructive',
      });
    } finally {
      setDeletingIds(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const handleConfirmRegistration = async (item: NotificationItem) => {
    const registrationId = item.attributes.registration_id;
    if (!registrationId) return;

    setConfirmingIds(prev => new Set(prev).add(item.id));
    try {
      await scheduleEventsService.confirmRegistration(registrationId);
      // Hide the confirm button by clearing the pending flag on the item.
      setItems(prev =>
        prev.map(i =>
          i.id === item.id ? { ...i, attributes: { ...i.attributes, requires_confirmation: false } } : i
        )
      );
      toast({ title: t('schedule.notifications.confirmSuccess'), duration: 3000 });
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setConfirmingIds(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  if (!isAuthenticated) return null;

  const hasUnread = items.some(i => !i.attributes.viewed);

  return (
    <div className="w-full max-w-3xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('notifications.title')}</h1>
        {hasUnread && (
          <Button variant="outline" size="sm" onClick={handleMarkAllRead} disabled={markingAll}>
            {t('notifications.markAllRead')}
          </Button>
        )}
      </div>

      {/* Whoever opens this list cares what is on it: the best moment to offer
          it outside the app too. */}
      <PushNudgeCard placement="notifications" />

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <CardContent className="p-3">
                <div className="flex gap-3">
                  <div className="w-16 h-16 bg-muted animate-pulse rounded-md flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted animate-pulse rounded w-2/3" />
                    <div className="h-3 bg-muted animate-pulse rounded w-1/2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            {t('notifications.empty')}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {items.map(item => (
              <FeedItemCard
                key={item.id}
                item={item}
                unread={!item.attributes.viewed}
                onClick={() => handleItemClick(item)}
                actions={
                  item.type === 'climb_reported_removed' ? (
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={deletingIds.has(item.id)}
                      onClick={e => {
                        e.stopPropagation();
                        handleDeleteClimb(item);
                      }}
                    >
                      {t('notifications.climbReportedRemoved.deleteAction')}
                    </Button>
                  ) : item.type === 'schedule_registration_created' &&
                    item.attributes.requires_confirmation &&
                    item.attributes.registration_id ? (
                    <Button
                      size="sm"
                      disabled={confirmingIds.has(item.id)}
                      onClick={e => {
                        e.stopPropagation();
                        handleConfirmRegistration(item);
                      }}
                    >
                      {t('schedule.confirm')}
                    </Button>
                  ) : undefined
                }
              />
            ))}
          </div>

          {hasMore && (
            <div ref={loadMoreRef} className="py-4">
              {loadingMore && (
                <Card>
                  <CardContent className="p-3">
                    <div className="flex gap-3">
                      <div className="w-16 h-16 bg-muted animate-pulse rounded-md flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-muted animate-pulse rounded w-2/3" />
                        <div className="h-3 bg-muted animate-pulse rounded w-1/2" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
